// components/EmployeeModal.js

import imageCompression from 'browser-image-compression';
import Image from "components/appImage";
import Modal from "components/ui/modal";
import { useEffect, useRef, useState } from "react";
import Icon from "../../../components/appIcon";
import Button from "../../../components/ui/button";
import Input from "../../../components/ui/input";
import Select from "../../../components/ui/select";
import employeeApi from "services/api/employeeApi";

const BACKEND_URL = import.meta.env.VITE_API_URL;

const EmployeeModal = ({
  employee,
  isOpen,
  onClose,
  onSave,
  mode,
  setModalState,
  showNotification,
  isEditing,
  setIsEditing,
  departments
}) => {
  console.log("employee", employee);
  const [loading, setLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [images, setImages] = useState({});
  const [selectedFiles, setSelectedFiles] = useState({});
  const [errors, setErrors] = useState({});
  const [cameraOpen, setCameraOpen] = useState(false);
  const [currentPosition, setCurrentPosition] = useState(null);
  const [activeImagePosition, setActiveImagePosition] = useState(null);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [croppingOpen, setCroppingOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const cropCanvasRef = useRef(null);
  const imageRef = useRef(null);
  const [cropBox, setCropBox] = useState({
    x: 50,
    y: 50,
    width: 200,
    height: 200
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);

  const [imageDimensions, setImageDimensions] = useState({
    naturalWidth: 0,
    naturalHeight: 0,
    displayWidth: 0,
    displayHeight: 0,
    scaleX: 1,
    scaleY: 1
  });

  useEffect(()=>{
    console.log("images",images)
  },[images])
  // Dynamic image management
  const [imageRows, setImageRows] = useState([[]]); // Array of rows, each row is an array of image positions
  const IMAGES_PER_ROW = 4; // Number of images per row

  const [formData, setFormData] = useState({
    employee_id: "",
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    password: "",
    date_of_join: "",
    employee_type: "",
    department: "",
  });

  // Create department options from props
  const departmentOptions = departments.map(dept => ({
    value: dept.id,
    label: dept.name
  }));

  const employeeTypeOptions = [
    { value: "permanent", label: "Permanent" },
    { value: "temporary", label: "Temporary" }
  ];

  // Initialize the images object with all positions
  useEffect(() => {
    if (!isOpen) {
      setActiveImagePosition(null);
      return;
    }

    if (mode === "add") {
      // Start with one empty row
      setImageRows([[]]);
      setImages({});

      const initialFormData = {
        employee_id: "",
        first_name: "",
        last_name: "",
        email: "",
        phone_number: "",
        password: "",
        date_of_join: "",
        employee_type: "",
        department: "",
      };

      setFormData(initialFormData);
      setSelectedFiles({});
      setErrors({});
      setIsEditing(true);
    } else if (employee) {
      // Process the images array from the backend
      const employeeImagesArr = employee.images || [];
      const employeeImagesObj = {};
      const formDataImages = {};

      // Create image rows based on existing images
      const rows = [];
      const tempImages = {};

      // Process existing images and organize them into rows
      employeeImagesArr.forEach((imageObj, index) => {
        const position = `image${index + 1}`;
        // Extract the imagePath from the image object
        const imagePath = imageObj.imagePath;
        const imageSrc = imagePath ? `${BACKEND_URL}${imagePath}` : "";

        tempImages[position] = imageSrc;
        formDataImages[position] = imagePath;
        // Store the image ID as well
        formDataImages[`${position}_id`] = imageObj.id;

        // Calculate which row this image belongs to
        const rowIndex = Math.floor(index / IMAGES_PER_ROW);
        const positionInRow = index % IMAGES_PER_ROW;

        // Ensure the row exists
        while (rows.length <= rowIndex) {
          rows.push([]);
        }

        // Add the position to the appropriate row
        rows[rowIndex][positionInRow] = position;
      });

      // Fill in any empty positions in the last row
      if (rows.length > 0) {
        const lastRow = rows[rows.length - 1];
        while (lastRow.length < IMAGES_PER_ROW) {
          const newPosition = `image${Object.keys(tempImages).length + 1}`;
          lastRow.push(newPosition);
          tempImages[newPosition] = "";
        }
      }

      // If no images, create one empty row
      if (rows.length === 0) {
        rows.push(["image1", "image2", "image3", "image4"]);
        for (let i = 1; i <= 4; i++) {
          tempImages[`image${i}`] = "";
        }
      }

      setImageRows(rows);
      setImages(tempImages);
      setSelectedFiles({});
      setErrors({});

      // Find department ID if department name is provided
      let departmentValue = "";
      if (employee.department) {
        const dept = departments.find(d => d.name === employee.department || d.id === employee.department);
        departmentValue = dept ? dept.id : employee.department;
      }

      setFormData({
        employee_id: employee.employee_id || "",
        first_name: employee.first_name || "",
        last_name: employee.last_name || "",
        email: employee.email || "",
        phone_number: employee.phone_number || "",
        password: "",
        date_of_join: employee.date_of_join ? employee.date_of_join.split("T")[0] : "",
        employee_type: employee.employee_type || "",
        department: departmentValue,
        ...formDataImages
      });

      setIsEditing(false);
    }
  }, [isOpen, mode, employee, departments]);

  useEffect(() => {
    if (mode === "edit") {
      setIsEditing(true);
    }
  }, [mode]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const validationErrors = {};

    if (!formData.first_name.trim()) validationErrors.first_name = "First name is required";
    if (!formData.last_name.trim()) validationErrors.last_name = "Last name is required";
    if (!formData.email.trim()) validationErrors.email = "Email is required";
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      validationErrors.email = "Invalid email format";
    if (!formData.phone_number.trim()) validationErrors.phone_number = "Phone number is required";
    if (formData.phone_number && !/^[0-9]{10}$/.test(formData.phone_number))
      validationErrors.phone_number = "Phone number must be 10 digits";
    if (!formData.department) validationErrors.department = "Department is required";
    if (!formData.employee_type) validationErrors.employee_type = "Employment type is required";
    if (!formData.date_of_join) validationErrors.date_of_join = "Date of joining is required";

    if (mode === "add") {
      if (!formData.password) {
        validationErrors.password = "Password is required";
      } else if (formData.password.length < 6) {
        validationErrors.password = "Password must be at least 6 characters long";
      }
    }

    // Count total images
    const selectedImageCount = Object.values(selectedFiles)
      .filter(file => file instanceof File).length;

    const existingImageCount = Object.values(images)
      .filter(img => img && !img.startsWith("blob:")).length;

    const totalImages = selectedImageCount + existingImageCount;

    if (mode === "add" && totalImages < 4) {
      validationErrors.images = "At least 4 images are required";
    }

    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showNotification("Please fix errors before saving", "error");
      return;
    }

    setLoading(true);
    try {
      let submissionData;

      if (mode === 'add') {
        submissionData = new FormData();
        // Add all form fields
        submissionData.append('first_name', formData.first_name);
        submissionData.append('last_name', formData.last_name);
        submissionData.append('email', formData.email);
        submissionData.append('password', formData.password);
        submissionData.append('phone_number', formData.phone_number);
        submissionData.append('date_of_join', formData.date_of_join);
        submissionData.append('department', formData.department);
        submissionData.append('employee_type', formData.employee_type);

        // Add all images to form data
        Object.entries(selectedFiles).forEach(([position, file]) => {
          submissionData.append('images', file); // Use 'images' as the key to match backend
        });
      } else {
        submissionData = new FormData();
        const dataObj = {};
        Object.entries(formData).forEach(([key, value]) => {
          if (!key.startsWith('image') && !key.endsWith('_id')) {
            dataObj[key] = value;
          }
        });
        submissionData.append('data', JSON.stringify(dataObj));

        // Add all new/updated images
        Object.entries(selectedFiles).forEach(([position, file]) => {
          submissionData.append('files', file);
        });
      }

      await onSave(submissionData);
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setActiveImagePosition(null);
    onClose();
    setSelectedFiles({});
    setErrors({});
    setIsEditing(false);
  };

  // Function to delete a single image
  const handleDeleteImage = async (position) => {
    // Check if this is an existing image (not a newly uploaded one)
    const imageId = formData[`${position}_id`];

    try {
      // If it's an existing image, call the API to delete it
      if (imageId) {
        await employeeApi.deleteEmployeeImage(imageId);
        showNotification("Image deleted successfully", "success");
      }

      // Remove from selected files if it exists
      const newSelectedFiles = { ...selectedFiles };
      if (newSelectedFiles[position]) {
        delete newSelectedFiles[position];
        setSelectedFiles(newSelectedFiles);
      }

      // Remove from images if it exists
      const newImages = { ...images };
      if (newImages[position]) {
        // Revoke the object URL to avoid memory leaks
        if (newImages[position].startsWith("blob:")) {
          URL.revokeObjectURL(newImages[position]);
        }
        newImages[position] = "";
        setImages(newImages);
      }

      // Also clear from form data
      setFormData(prev => ({
        ...prev,
        [position]: "",
        [`${position}_id`]: ""
      }));
    } catch (error) {
      console.error("Error deleting image:", error);
      showNotification("Failed to delete image", "error");
    }
  };

  // Function to delete an entire row of images
  const handleRemoveImageRow = async (rowIndex) => {
    const newImageRows = [...imageRows];
    const removedRow = newImageRows[rowIndex];

    // Collect image IDs for existing images in this row
    const imageIdsToDelete = [];

    // Remove images from the row
    removedRow.forEach(position => {
      // Check if this is an existing image (not a newly uploaded one)
      const imageId = formData[`${position}_id`];
      if (imageId) {
        // Convert to string to match backend validation
        imageIdsToDelete.push(String(imageId));
      }

      if (images[position]) {
        // Revoke the object URL to avoid memory leaks
        if (images[position].startsWith("blob:")) {
          URL.revokeObjectURL(images[position]);
        }

        // Remove from images and selectedFiles
        const newImages = { ...images };
        delete newImages[position];
        setImages(newImages);

        const newSelectedFiles = { ...selectedFiles };
        if (newSelectedFiles[position]) {
          delete newSelectedFiles[position];
          setSelectedFiles(newSelectedFiles);
        }

        // Also clear from form data
        const newFormData = { ...formData };
        if (newFormData[position]) {
          delete newFormData[position];
        }
        if (newFormData[`${position}_id`]) {
          delete newFormData[`${position}_id`];
        }
        setFormData(newFormData);
      }
    });

    // Remove the row
    newImageRows.splice(rowIndex, 1);
    setImageRows(newImageRows);

    // Delete existing images from the backend
    if (imageIdsToDelete.length > 0) {
      try {
        await employeeApi.deleteEmployeeImagesBulk(imageIdsToDelete);
        showNotification("Images deleted successfully", "success");
      } catch (error) {
        console.error("Error deleting images:", error);
        showNotification("Failed to delete some images", "error");
      }
    } else {
      // Show notification for newly uploaded images only
      showNotification("Image row removed successfully", "success");
    }
  };

  const compressImage = async (file) => {
    if (file.size > 2 * 1024 * 1024) { // If file is larger than 2MB
      try {
        console.log(`Original file size: ${(file.size / 1024 / 1024).toFixed(2)} MB`);

        // Primary compression settings - more aggressive
        const options = {
          maxSizeMB: 1, // Target 1MB (half of original threshold)
          maxWidthOrHeight: 1280, // Reduce dimensions
          useWebWorker: true, // Use web worker for faster compression
          fileType: 'image/jpeg', // Convert to JPEG for better compression
          quality: 0.7, // Quality setting (0 to 1)
        };

        let compressedFile = await imageCompression(file, options);
        console.log(`First compression result: ${(compressedFile.size / 1024 / 1024).toFixed(2)} MB`);

        // If still larger than 1.5MB, try more aggressive compression
        if (compressedFile.size > 1.5 * 1024 * 1024) {
          const aggressiveOptions = {
            maxSizeMB: 0.8, // Target 800KB
            maxWidthOrHeight: 1024, // Further reduce dimensions
            useWebWorker: true,
            fileType: 'image/jpeg',
            quality: 0.6, // Lower quality
          };

          compressedFile = await imageCompression(compressedFile, aggressiveOptions);
          console.log(`Second compression result: ${(compressedFile.size / 1024 / 1024).toFixed(2)} MB`);
        }

        // Final check - if still too large, try extreme compression
        if (compressedFile.size > 1.2 * 1024 * 1024) {
          const extremeOptions = {
            maxSizeMB: 0.5, // Target 500KB
            maxWidthOrHeight: 800, // Even smaller dimensions
            useWebWorker: true,
            fileType: 'image/jpeg',
            quality: 0.5, // Very low quality
          };

          compressedFile = await imageCompression(compressedFile, extremeOptions);
          console.log(`Final compression result: ${(compressedFile.size / 1024 / 1024).toFixed(2)} MB`);
        }

        return compressedFile;
      } catch (error) {
        console.error("Error compressing image:", error);

        // If compression fails, try with basic settings
        try {
          const fallbackOptions = {
            maxSizeMB: 2,
            maxWidthOrHeight: 1920,
            useWebWorker: false,
          };
          const compressedFile = await imageCompression(file, fallbackOptions);
          console.log(`Fallback compression result: ${(compressedFile.size / 1024 / 1024).toFixed(2)} MB`);
          return compressedFile;
        } catch (fallbackError) {
          console.error("Fallback compression also failed:", fallbackError);
          showNotification("Image compression failed. Using original image.", "warning");
          return file; // Return original file if all compression attempts fail
        }
      }
    }
    return file; // Return original file if it's already under 2MB
  };

  const handleImageFileSelect = async (position, file) => {
    if (!file.type.match('image.*')) {
      setErrors(prev => ({ ...prev, images: "Only image files are allowed" }));
      return;
    }

    try {
      // Show initial file size
      const originalSize = (file.size / 1024 / 1024).toFixed(2);
      console.log(`Processing ${file.name}, original size: ${originalSize} MB`);

      // Compress the image if it's larger than 2MB
      let processedFile = file;
      if (file.size > 2 * 1024 * 1024) {
        showNotification("Compressing large image...", "info");
        processedFile = await compressImage(file);
        const compressedSize = (processedFile.size / 1024 / 1024).toFixed(2);
        console.log(`Compressed size: ${compressedSize} MB`);

        // Show compression result to user
        const reduction = ((file.size - processedFile.size) / file.size * 100).toFixed(0);
        showNotification(
          `Image compressed successfully! Reduced by ${reduction}% (${originalSize}MB → ${compressedSize}MB)`,
          "success"
        );
      }

      setSelectedFiles(prev => ({ ...prev, [position]: processedFile }));
      setImages(prev => ({ ...prev, [position]: URL.createObjectURL(processedFile) }));

      if (errors.images) {
        setErrors(prev => ({ ...prev, images: "" }));
      }
    } catch (error) {
      console.error("Error processing image:", error);
      showNotification("Failed to process image. Please try again.", "error");
    }
  };

  const handleImageSelect = (event, position) => {
    const file = event.target.files?.[0];
    if (!file) return;
    handleImageFileSelect(position, file);
  };

  // Function to open image preview
  const handleImagePreview = (imageSrc) => {
    setPreviewImage(imageSrc);
    setImagePreviewOpen(true);
  };

  // Function to add a new row of images
  const handleAddImageRow = () => {
    const newImages = { ...images };
    const newRow = [];

    // Create 4 new image positions
    for (let i = 0; i < IMAGES_PER_ROW; i++) {
      const position = `image${Object.keys(newImages).length + 1}`;
      newRow.push(position);
      newImages[position] = "";
    }

    setImages(newImages);
    setImageRows(prev => [...prev, newRow]);
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: "user"
        }
      });
      setStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error("Error accessing camera:", err);
      showNotification("Could not access camera. Please check permissions.", "error");
      setCameraOpen(false);
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  };

  const captureImage = () => {
    if (videoRef.current && canvasRef.current) {
      const context = canvasRef.current.getContext('2d');
      canvasRef.current.width = videoRef.current.videoWidth;
      canvasRef.current.height = videoRef.current.videoHeight;
      context.drawImage(videoRef.current, 0, 0);
      const imageDataUrl = canvasRef.current.toDataURL('image/png');
      setCapturedImage(imageDataUrl);
      setImageToCrop(imageDataUrl);
      setCroppingOpen(true);
    }
  };

  const handleCrop = () => {
    if (imageToCrop && imageRef.current && cropCanvasRef.current) {
      const image = imageRef.current;
      const canvas = cropCanvasRef.current;
      const ctx = canvas.getContext('2d');

      // Set canvas size to crop box dimensions
      canvas.width = cropBox.width;
      canvas.height = cropBox.height;

      // Draw the cropped portion of the image
      ctx.drawImage(
        image,
        cropBox.x, cropBox.y, // Source x, y
        cropBox.width, cropBox.height, // Source width, height
        0, 0, // Destination x, y
        cropBox.width, cropBox.height // Destination width, height
      );

      // Get the cropped image as data URL
      const croppedImageUrl = canvas.toDataURL('image/png');
      setCapturedImage(croppedImageUrl);
      setCroppingOpen(false);

      // Convert to file and save to the current position
      fetch(croppedImageUrl)
        .then(res => res.blob())
        .then(blob => {
          const file = new File([blob], `capture-${currentPosition}.png`, { type: 'image/png' });
          handleImageFileSelect(currentPosition, file);
          setCameraOpen(false);
          setCapturedImage(null);
          setActiveImagePosition(null);
        })
        .catch(error => {
          console.error("Error processing cropped image:", error);
          showNotification("Failed to process cropped image. Please try again.", "error");
        });
    }
  };

  const openCameraForPosition = (position) => {
    setCurrentPosition(position);
    setCameraOpen(true);
    setCapturedImage(null);
  };

  const handleEditButton = () => {
    setIsEditing(true);
    setModalState((prev) => ({ ...prev, mode: "edit" }));
  };

  // Crop box dragging handlers
  const handleMouseDown = (e, handle) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    setResizeHandle(handle);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    const dx = e.clientX - dragStart.x;
    const dy = e.clientY - dragStart.y;

    setCropBox(prev => {
      let newX = prev.x;
      let newY = prev.y;
      let newWidth = prev.width;
      let newHeight = prev.height;

      if (resizeHandle === 'move') {
        newX = prev.x + dx;
        newY = prev.y + dy;
      } else if (resizeHandle === 'se') {
        newWidth = Math.max(50, prev.width + dx);
        newHeight = Math.max(50, prev.height + dy);
      } else if (resizeHandle === 's') {
        newHeight = Math.max(50, prev.height + dy);
      } else if (resizeHandle === 'e') {
        newWidth = Math.max(50, prev.width + dx);
      }

      // Ensure crop box stays within image bounds
      if (imageRef.current) {
        const imgWidth = imageRef.current.naturalWidth;
        const imgHeight = imageRef.current.naturalHeight;

        newX = Math.max(0, Math.min(newX, imgWidth - newWidth));
        newY = Math.max(0, Math.min(newY, imgHeight - newHeight));

        if (newWidth + newX > imgWidth) {
          newWidth = imgWidth - newX;
        }

        if (newHeight + newY > imgHeight) {
          newHeight = imgHeight - newY;
        }
      }

      return { x: newX, y: newY, width: newWidth, height: newHeight };
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setResizeHandle(null);
  };

  // Initialize crop box when image loads
  const handleImageLoad = () => {
    if (imageRef.current) {
      const img = imageRef.current;
      const naturalWidth = img.naturalWidth;
      const naturalHeight = img.naturalHeight;
      const displayWidth = img.clientWidth;
      const displayHeight = img.clientHeight;

      // Calculate scale factors
      const scaleX = naturalWidth / displayWidth;
      const scaleY = naturalHeight / displayHeight;

      setImageDimensions({
        naturalWidth,
        naturalHeight,
        displayWidth,
        displayHeight,
        scaleX,
        scaleY
      });

      // Initialize crop box to center of image
      const size = Math.min(naturalWidth, naturalHeight) * 0.6; // 60% of smaller dimension
      setCropBox({
        x: (naturalWidth - size) / 2,
        y: (naturalHeight - size) / 2,
        width: size,
        height: size
      });
    }
  };

  useEffect(() => {
    if (cameraOpen) {
      startCamera();
    } else {
      stopCamera();
    }

    return () => {
      stopCamera();
    };
  }, [cameraOpen]);

  if (!isOpen) return null;

  // Calculate display crop box dimensions
  const displayCropBox = {
    x: cropBox.x / imageDimensions.scaleX,
    y: cropBox.y / imageDimensions.scaleY,
    width: cropBox.width / imageDimensions.scaleX,
    height: cropBox.height / imageDimensions.scaleY
  };

  // Count total images
  const selectedImageCount = Object.values(selectedFiles)
    .filter(file => file instanceof File).length;

  const existingImageCount = Object.values(images)
    .filter(img => img && !img.startsWith("blob:")).length;

  const totalImages = selectedImageCount + existingImageCount;

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <Icon name={mode === "add" ? "UserPlus" : isEditing ? "Edit" : "User"} size={24} className="text-primary" />
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                {mode === "add" ? "Add New Employee" : isEditing ? "Edit Employee" : "Employee Details"}
              </h2>
              {employee && <p className="text-sm text-muted-foreground font-mono">{employee.employee_id}</p>}
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Form Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="First Name"
              value={formData.first_name}
              onChange={(e) => handleInputChange("first_name", e.target.value)}
              disabled={!isEditing}
              required
              error={errors.first_name}
            />
            <Input
              label="Last Name"
              value={formData.last_name}
              onChange={(e) => handleInputChange("last_name", e.target.value)}
              disabled={!isEditing}
              required
              error={errors.last_name}
            />
            <Input
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              disabled={!isEditing}
              required
              error={errors.email}
            />
            {mode === "add" && (
              <Input
                label="Password"
                type="password"
                value={formData.password}
                onChange={(e) => handleInputChange("password", e.target.value)}
                disabled={!isEditing}
                required
                error={errors.password}
              />
            )}
            <Input
              label="Phone"
              type="number"
              value={formData.phone_number}
              onChange={(e) => handleInputChange("phone_number", e.target.value)}
              disabled={!isEditing}
              error={errors.phone_number}
            />
            <Select
              label="Department"
              options={departmentOptions}
              value={formData.department}
              onChange={(v) => handleInputChange("department", v)}
              disabled={!isEditing}
              error={errors.department}
              placeholder="Select department"
            />
            <Select
              label="Employment Type"
              options={employeeTypeOptions}
              value={formData.employee_type}
              onChange={(v) => handleInputChange("employee_type", v)}
              disabled={!isEditing}
              error={errors.employee_type}
            />
            <Input
              label="Date of Joining"
              type="date"
              value={formData.date_of_join ? formData.date_of_join.split("T")[0] : ""}
              onChange={(e) => handleInputChange("date_of_join", e.target.value)}
              disabled={!isEditing}
              error={errors.date_of_join}
            />
          </div>

          {/* Image Upload Section */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium">Employee Images</h3>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-muted-foreground">
                  {totalImages} images
                </span>
              </div>
            </div>

            {/* Image Rows */}
            {imageRows.map((row, rowIndex) => (
              <div key={rowIndex} className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Row {rowIndex + 1}</span>
                  {isEditing && imageRows.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveImageRow(rowIndex)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Icon name="Trash" size={16} className="mr-1" />
                      Remove Row
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {row.map((pos) => {
                    const hasImage = images[pos] && images[pos] !== "";
                    const isNewImage = selectedFiles[pos] instanceof File;

                    return (
                      <div key={pos} className="relative group">
                        <div
                          className={`w-full aspect-square rounded-lg overflow-hidden bg-gray-100 shadow-inner cursor-pointer border-2 ${hasImage ? 'border-gray-300' : 'border-dashed border-gray-400'}`}
                          onClick={() => hasImage ? handleImagePreview(images[pos]) : (isEditing && setActiveImagePosition(activeImagePosition === pos ? null : pos))}
                        >
                          <Image
                            src={images[pos] || `https://via.placeholder.com/150?text=${pos}`}
                            alt={`${pos} view`}
                            className="w-full h-full object-cover"
                          />
                        </div>

                        {/* Image options overlay - shown on hover or when active */}
                        {isEditing && (
                          <div
                            className={`absolute inset-0 rounded-lg bg-black/70 transition-all duration-200 flex flex-col items-center justify-center p-2
              ${activeImagePosition === pos ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                          >
                            <div className="flex flex-col space-y-2 w-full">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  document.getElementById(`file-input-${pos}`).click();
                                }}
                                className="text-white flex items-center justify-center text-xs p-1 rounded hover:bg-white/20"
                                disabled={isUploading}
                              >
                                <Icon name="Upload" size={16} className="mr-1" />
                                <span>Upload</span>
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openCameraForPosition(pos);
                                }}
                                className="text-white flex items-center justify-center text-xs p-1 rounded hover:bg-white/20"
                                disabled={isUploading}
                              >
                                <Icon name="Camera" size={16} className="mr-1" />
                                <span>Capture</span>
                              </button>
                              {hasImage && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteImage(pos);
                                  }}
                                  className="text-white flex items-center justify-center text-xs p-1 rounded hover:bg-white/20"
                                  disabled={isUploading}
                                >
                                  <Icon name="Trash" size={16} className="mr-1" />
                                  <span>Delete</span>
                                </button>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Close button for images */}
                        {hasImage && isEditing && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteImage(pos);
                            }}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            disabled={isUploading}
                          >
                            <Icon name="X" size={12} />
                          </button>
                        )}

                        {/* Image indicator */}
                        <div className="flex items-center mt-1">
                          {hasImage && (
                            <>
                              {isNewImage && (
                                <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mr-1" title="New image"></span>
                              )}
                              <span className="text-xs text-gray-500">{pos.replace('image', '')}</span>
                            </>
                          )}
                          {!hasImage && (
                            <span className="text-xs text-gray-400">{pos.replace('image', '')}</span>
                          )}
                        </div>

                        <input
                          id={`file-input-${pos}`}
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageSelect(e, pos)}
                          className="hidden"
                          disabled={isUploading || !isEditing}
                        />
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}

            {/* Add More Images Button */}
            {isEditing && (
              <div className="flex justify-center mt-6">
                <Button
                  variant="outline"
                  onClick={handleAddImageRow}
                  disabled={isUploading}
                  className="flex items-center"
                >
                  <Icon name="Plus" size={16} className="mr-2" />
                  Add {IMAGES_PER_ROW} More Images
                </Button>
              </div>
            )}

            {/* Image validation error */}
            {errors.images && (
              <div className="text-red-500 text-sm mt-4 text-center">
                {errors.images}
              </div>
            )}

            {/* Save Images Button */}
            {(isEditing && mode !== "add") && (
              <div className="flex space-x-3 mt-6">
                <Button
                  variant="solid"
                  size="md"
                  className="bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 text-white flex-1 p-2 rounded"
                  onClick={handleSave}
                  disabled={isUploading}
                  iconName="Save"
                >
                  Upload Images
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border bg-muted/30">
          <div className="flex flex-col text-xs text-muted-foreground space-y-1">
            {employee && !isEditing && (
              <>
                {employee.date_of_join && <span>Joined: {new Date(employee.date_of_join).toLocaleDateString()}</span>}
                {employee.updatedAt && <span>Last Updated: {new Date(employee.updatedAt).toLocaleString()}</span>}
              </>
            )}
          </div>
          <div className="flex items-center space-x-3">
            {isEditing ? (
              <>
                <Button variant="outline" onClick={handleCancel} disabled={loading}>Cancel</Button>
                <Button onClick={handleSave} loading={loading}>
                  {mode === "add" ? "Add Employee" : "Save Changes"}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={onClose}>Close</Button>
                {mode !== "add" && (
                  <Button onClick={handleEditButton}>
                    <Icon name="Edit" size={16} className="mr-2" />Edit Employee
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </Modal>

      {/* Camera Modal */}
      <Modal isOpen={cameraOpen} onClose={() => { setCameraOpen(false); stopCamera(); }}>
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4">Capture Image with Laptop Camera</h3>

          <div className="flex flex-col items-center">
            <div className="relative w-full max-w-md rounded-lg overflow-hidden mb-4 bg-black">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                className="w-full h-auto"
              />
              {!stream && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                  <div className="text-white text-center p-4">
                    <Icon name="CameraOff" size={48} className="mx-auto mb-2" />
                    <p>Camera access denied or unavailable</p>
                  </div>
                </div>
              )}
            </div>
            <div className="flex space-x-3">
              <Button
                onClick={captureImage}
                className="bg-blue-600 hover:bg-blue-700"
                disabled={!stream}
              >
                <Icon name="Camera" size={16} className="mr-2" />Capture
              </Button>
              <Button variant="outline" onClick={() => { setCameraOpen(false); stopCamera(); }}>
                Cancel
              </Button>
            </div>
          </div>

          <canvas ref={canvasRef} className="hidden" />
        </div>
      </Modal>

      {/* Cropping Modal */}
      <Modal isOpen={croppingOpen} onClose={() => setCroppingOpen(false)}>
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4">Crop Image</h3>

          <div className="mb-4 relative overflow-hidden border border-gray-300 rounded-lg"
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}>
            {imageToCrop && (
              <>
                <img
                  ref={imageRef}
                  src={imageToCrop}
                  alt="To crop"
                  className="max-w-full max-h-80 object-contain"
                  onLoad={handleImageLoad}
                />

                {/* Crop box */}
                <div
                  className="absolute border-2 border-blue-500 bg-blue-500 bg-opacity-20"
                  style={{
                    left: `${displayCropBox.x}px`,
                    top: `${displayCropBox.y}px`,
                    width: `${displayCropBox.width}px`,
                    height: `${displayCropBox.height}px`,
                    cursor: 'move'
                  }}
                  onMouseDown={(e) => handleMouseDown(e, 'move')}
                />

                {/* Resize handles */}
                <div
                  className="absolute w-3 h-3 bg-blue-500 rounded-full cursor-se-resize"
                  style={{
                    left: `${displayCropBox.x + displayCropBox.width - 6}px`,
                    top: `${displayCropBox.y + displayCropBox.height - 6}px`
                  }}
                  onMouseDown={(e) => handleMouseDown(e, 'se')}
                />

                <div
                  className="absolute w-6 h-3 bg-blue-500 rounded cursor-s-resize"
                  style={{
                    left: `${displayCropBox.x + displayCropBox.width / 2 - 12}px`,
                    top: `${displayCropBox.y + displayCropBox.height - 6}px`
                  }}
                  onMouseDown={(e) => handleMouseDown(e, 's')}
                />

                <div
                  className="absolute w-3 h-6 bg-blue-500 rounded cursor-e-resize"
                  style={{
                    left: `${displayCropBox.x + displayCropBox.width - 6}px`,
                    top: `${displayCropBox.y + displayCropBox.height / 2 - 12}px`
                  }}
                  onMouseDown={(e) => handleMouseDown(e, 'e')}
                />
              </>
            )}
          </div>

          <div className="flex space-x-3">
            <Button onClick={handleCrop} className="bg-green-600 hover:bg-green-700">
              <Icon name="Crop" size={16} className="mr-2" />Crop Image
            </Button>
            <Button variant="outline" onClick={() => setCroppingOpen(false)}>
              Cancel
            </Button>
          </div>

          <canvas ref={cropCanvasRef} className="hidden" />
        </div>
      </Modal>

      {/* Image Preview Modal */}
      <Modal isOpen={imagePreviewOpen} onClose={() => setImagePreviewOpen(false)} size="lg">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Image Preview</h3>
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setImagePreviewOpen(false);
              }}
            >
              <Icon name="X" size={20} />
            </Button>
          </div>
          <div className="flex items-center justify-center">
            <img
              src={previewImage}
              alt="Preview"
              className="max-w-full max-h-96 object-contain"
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default EmployeeModal;